/**
 * Task Automation ("Tasks" / Ballerine Studio) — shared contract constants (BAL-7917).
 *
 * Single source of truth for the string-literal domains behind the `workforce_*` tables.
 * The Drizzle `pgEnum`s (unified-api `workforce-schema.ts`) and the FE Zod / BE TypeBox
 * schemas all reference THESE arrays, so the DB enum, the API contract, and the UI can never
 * drift. `risk_type` is deliberately NOT a pgEnum — it is a `varchar` validated at write against
 * {@link WORKFORCE_RISK_TYPES} (a new risk type is a code change, not an `ALTER TYPE`).
 *
 * DB-prefix `workforce_` (the existing `tasks` table is the CSV-batch one); product/display name
 * is "Tasks". See `docs/plans/BAL-7917-task-automation-backend.md` (locked schema).
 */

// Lifecycle — standard Kanban (K13). Terminal = { done, cancelled }. FE↔BE status is 1:1 (B11).
// pending_review/in_review (BAL-8059, four-eye): worker finishes → pending_review; the OWNER
// picks it up → in_review → done (or sends back to in_progress). Review is OPT-IN — the direct
// in_progress → done path stays legal. Appended (not inserted) so the migration is a plain
// `ALTER TYPE … ADD VALUE` — pgEnum order is not load-bearing anywhere.
export const WORKFORCE_TASK_STATUSES = [
  'to_do',
  'in_progress',
  'done',
  'cancelled',
  'pending_review',
  'in_review',
] as const;
export type WorkforceTaskStatus = (typeof WORKFORCE_TASK_STATUSES)[number];

// A task in one of these is closed; leaving one (terminal → active) is a "reopen" (B7).
export const WORKFORCE_TERMINAL_STATUSES = [
  'done',
  'cancelled',
] as const satisfies readonly WorkforceTaskStatus[];

// BAL-8357: the reports-table "Task Owner" filter puts users and teams in ONE multi-select, so
// each selected value carries its kind as a prefix. The wire format is declared once here and
// consumed by all four layers that used to spell it out independently — the FE option builder,
// the FE Zod schema, the BE route's TypeBox `pattern`, and the BE decode — so a rename can't
// leave one of them silently matching nothing.
export const TASK_OWNER_PREFIXES = {
  user: 'user:',
  team: 'team:',
} as const;

export type TaskOwnerKind = keyof typeof TASK_OWNER_PREFIXES;

/** Unanchored at the end so it works as both a TypeBox `pattern` and a JS `RegExp` source. */
export const TASK_OWNER_VALUE_PATTERN = `^(${Object.keys(TASK_OWNER_PREFIXES).join('|')}):.+`;

export const formatTaskOwner = (kind: TaskOwnerKind, id: string) =>
  `${TASK_OWNER_PREFIXES[kind]}${id}`;

/** Splits the mixed selection back into the two id sets the repository filters on. */
export const parseTaskOwners = (values: readonly string[] | undefined) => {
  const taskAssigneeIds: string[] = [];
  const taskTeamIds: string[] = [];

  for (const value of values ?? []) {
    if (value.startsWith(TASK_OWNER_PREFIXES.user)) {
      taskAssigneeIds.push(value.slice(TASK_OWNER_PREFIXES.user.length));
    } else if (value.startsWith(TASK_OWNER_PREFIXES.team)) {
      taskTeamIds.push(value.slice(TASK_OWNER_PREFIXES.team.length));
    }
  }

  return { taskAssigneeIds, taskTeamIds };
};

// Open work first, then closed. ONE source for every "most interesting task first" ordering:
// the connected-tasks panel sort (workforce-tasks.service.ts) and the reports-table hover-card
// SQL (business-analysis-tasks.ts). `satisfies Record<WorkforceTaskStatus, number>` makes a new
// status a compile error here instead of silently sorting last on one surface only.
export const WORKFORCE_TASK_STATUS_RANK = {
  in_progress: 0,
  pending_review: 1,
  in_review: 2,
  to_do: 3,
  done: 4,
  cancelled: 5,
} as const satisfies Record<WorkforceTaskStatus, number>;

// The seven status BANDS of the tasks list (BAL-7934; product-team splits, 2026-08-03).
// A section is a PRESENTATION grouping over two API primitives — `status` + `assigneeKind` —
// not a first-class BE filter: `ai_working` is the agent-held slice of `in_progress`, and
// `in_progress` is the human-held (incl. unassigned) remainder. Shared here so the FE list,
// the FE filter bar, and any future BE `sections` param all read ONE mapping.
export const TASK_SECTION_KEYS = [
  'to_do',
  'in_progress',
  'ai_working',
  'pending_review',
  'in_review',
  'done',
  'cancelled',
] as const;
export type TaskSectionKey = (typeof TASK_SECTION_KEYS)[number];

/** Section → the `/tasks/list` + `/tasks/summary` filter primitives that materialize it. */
export const TASK_SECTION_DEFINITIONS: Record<
  TaskSectionKey,
  { statuses: readonly WorkforceTaskStatus[]; assigneeKind?: 'agent' | 'human' }
> = {
  to_do: { statuses: ['to_do'] },
  in_progress: { statuses: ['in_progress'], assigneeKind: 'human' },
  ai_working: { statuses: ['in_progress'], assigneeKind: 'agent' },
  pending_review: { statuses: ['pending_review'] },
  in_review: { statuses: ['in_review'] },
  done: { statuses: ['done'] },
  cancelled: { statuses: ['cancelled'] },
};

// Resolution — two fixed outcomes (K8/Δ16). Expandable later via `ALTER TYPE … ADD VALUE`.
export const WORKFORCE_TASK_RESOLUTIONS = ['cleared', 'flagged'] as const;
export type WorkforceTaskResolution = (typeof WORKFORCE_TASK_RESOLUTIONS)[number];

// Event — "what happened" (J8/Δ17). Rule listens on it (execution); template tags it (observability).
// Phase-1 wires 'report.published'; 'report.moved_to_qc'/'report.deleted' drive task cleanup; rest reserved.
export const WORKFORCE_EVENTS = [
  'report.published',
  'report.moved_to_qc',
  'report.deleted',
  'manual',
  'scheduled',
  'task.completed',
] as const;
export type WorkforceEvent = (typeof WORKFORCE_EVENTS)[number];

// Idempotency key-composition strategy — owned by the rule (J9). Default 'report'. No json-logic 'custom' (J15).
export const WORKFORCE_IDEMPOTENCY_SCOPES = ['report', 'report_risk_type', 'business'] as const;
export type WorkforceIdempotencyScope = (typeof WORKFORCE_IDEMPOTENCY_SCOPES)[number];

// Canonical risk taxonomy. Stored as VARCHAR, app-validated — NOT a pgEnum (B5). The first five MUST
// stay in lockstep with the keys of `RiskTypesSchema` (schemas/report-schema.ts) — DON'T hand-copy a
// separate vocabulary (E20); a parity unit test asserts it. `policyFit` is the separate policy-fit
// signal (not part of RiskTypesSchema). camelCase to match the schema keys.
export const WORKFORCE_RISK_TYPES = [
  'legal',
  'chargebacks',
  'reputational',
  'contentFindings',
  'transactionLaundering',
  'scamAndFraud',
  'policyFit',
] as const;
export type WorkforceRiskType = (typeof WORKFORCE_RISK_TYPES)[number];

// Decision (gates readiness when required) vs action (a do-a-thing side task).
export const WORKFORCE_TASK_KINDS = ['decision', 'action'] as const;
export type WorkforceTaskKind = (typeof WORKFORCE_TASK_KINDS)[number];

// Authoritative provenance of a task (Δ37) — the SIGNAL, replacing "manual = definition_id IS NULL".
// 'trigger' = the engine, 'manual' = a user-created task. A backfill replays publishes THROUGH the
// engine, so it stamps 'trigger' too (identical aging/idempotency) — no separate value. Read `origin`,
// never infer from nulls.
export const WORKFORCE_TASK_ORIGINS = ['trigger', 'manual'] as const;
export type WorkforceTaskOrigin = (typeof WORKFORCE_TASK_ORIGINS)[number];

// Condition det/LLM marker (K3). Phase-1 evaluates 'deterministic' only; 'llm' accepted-but-unbuilt.
export const WORKFORCE_CONDITION_TYPES = ['deterministic', 'llm'] as const;
export type WorkforceConditionType = (typeof WORKFORCE_CONDITION_TYPES)[number];

// Actor model (B12/F17) — who performed a mutation / owns a task / authored a comment.
// Phase-1 writes 'user' (session) + 'system' (the trigger); 'agent' is RESERVED for copilot (zero migration).
export const WORKFORCE_ACTOR_TYPES = ['user', 'agent', 'system'] as const;
export type WorkforceActorType = (typeof WORKFORCE_ACTOR_TYPES)[number];

// The actor performing a mutation / authoring a comment. `id` is null for the system actor.
export type WorkforceActor = { type: WorkforceActorType; id: string | null };

// HITL agent-execution policy per task type (F18). Phase-1 default 'forbidden'; 'propose'/'auto' reserved.
export const WORKFORCE_AGENT_EXECUTIONS = ['forbidden', 'propose', 'auto'] as const;
export type WorkforceAgentExecution = (typeof WORKFORCE_AGENT_EXECUTIONS)[number];

// Task↔task relationships. Phase-1 emits 'subtask' + 'follow_up'; the rest are human-authored links.
export const WORKFORCE_TASK_RELATIONSHIPS = [
  'subtask',
  'blocks',
  'blocked_by',
  'follow_up',
  'relates_to',
  'duplicate_of',
] as const;
export type WorkforceTaskRelationship = (typeof WORKFORCE_TASK_RELATIONSHIPS)[number];

// How a rule participates when several target the same report-type group (K18).
//   'all'  (default): fires independently iff its condition is truthy.
//   'first'         : priority-ordered; only the first truthy rule in the group fires.
export const WORKFORCE_TASK_RULE_MATCHES = ['first', 'all'] as const;
export type WorkforceTaskRuleMatch = (typeof WORKFORCE_TASK_RULE_MATCHES)[number];

// Explicit parent/child role, declared by the emit and stored on the task (D3). Readiness + the
// report overlay derive off `role`, NOT off `risk_type IS NULL` (never overload risk_type as "parent").
//   parent     — the alert-level decision task (fans out children)
//   subtask    — a child of a parent (the emit's relations wire it)
//   standalone — a task with no parent/child graph (manual tasks, default)
export const WORKFORCE_TASK_ROLES = ['parent', 'subtask', 'standalone'] as const;
export type WorkforceTaskRole = (typeof WORKFORCE_TASK_ROLES)[number];

// --- Automations authoring (BAL-8506 / BAL-8361 Wave 0) -----------------------------------------
// The decision-contract policy an authored automation applies when the agent proposes a verdict.
// Snapshotted onto the task at fire time (`workforce_tasks.on_*_action`); the resolution-apply step
// reads the task's copy, never the live definition (so BAL-8418 edits apply on next fire only).
//   set_flagged / set_cleared — write the terminal resolution autonomously (gated on
//                               outcomeCause === 'risk_finding' at apply time — degraded verdicts
//                               still route to review regardless of this selection).
//   send_to_review            — always hand the proposal to a human (the safe default).
export const WORKFORCE_ON_FLAG_ACTIONS = ['set_flagged', 'send_to_review'] as const;
export type WorkforceOnFlagAction = (typeof WORKFORCE_ON_FLAG_ACTIONS)[number];

export const WORKFORCE_ON_CLEAR_ACTIONS = ['set_cleared', 'send_to_review'] as const;
export type WorkforceOnClearAction = (typeof WORKFORCE_ON_CLEAR_ACTIONS)[number];

// How long the agent's task summary should be — a prompt-composition hint, not a hard limit.
export const WORKFORCE_SUMMARY_LENGTHS = ['brief', 'standard', 'detailed'] as const;
export type WorkforceSummaryLength = (typeof WORKFORCE_SUMMARY_LENGTHS)[number];

// Who does the work — the agent, or a human analyst. `manual` carries no agent_config (no
// capabilities/skills, no decision contract, no composed prompt); the task routes to the analyst
// queue with the instructions attached. Review-fix (2026-08-16): the create flow's task-type segment
// (ratified in the BAL-8361 design) had no wire representation, which would have made manual
// automations unrepresentable in the contract the FE builds against.
export const WORKFORCE_AUTOMATION_TASK_MODES = ['agentic', 'manual'] as const;
export type WorkforceAutomationTaskMode = (typeof WORKFORCE_AUTOMATION_TASK_MODES)[number];

// Typed audit-action catalog (Y4). `audit_logs.action` for entity_type='workforce_task' MUST be one of
// these; the audit row is written in the SAME txn as the mutation. This is a customer-visible contract
// via the audit endpoint — additive only (never rename/remove a value).
export const WORKFORCE_TASK_EVENTS = [
  'task.created',
  'task.status_changed',
  'task.resolution_set',
  'task.resolution_changed',
  'task.reopened',
  'task.cancelled',
  'task.deleted',
  'task.assignment_changed',
  'task.owner_changed',
  'task.comment_added',
  'task.attachment_added',
  'task.attachment_removed',
  'task.link_added',
  'task.link_removed',
] as const;
export type WorkforceTaskEvent = (typeof WORKFORCE_TASK_EVENTS)[number];
